// Wrapper for the materials workflow: quick picks, catalogue modal, and application to all faces or per face.
/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\Materiaux.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT MATÉRIAUX

📋 DESCRIPTION:
Composant React pour la sélection des matériaux dans le configurateur d'articles.
Interface avancée avec modes simple et personnalisé pour sélection matières/couleurs.

🎯 RESPONSABILITÉS:
- Sélection des matières et couleurs pour les différentes faces d'un article
- Double mode : simple (même matière/couleur partout) et personnalisé (par face)
- Interface adaptative selon le type d'article (coussin ou bâche)
- Gestion du stock matières avec option "en stock"
- Sélection visuelle avec aperçu couleurs et prix
- Synchronisation automatique entre faces identiques

🔗 DÉPENDANCES:
- types.ts: Interfaces Article et Matiere pour typage
- React: Hooks useState, useEffect pour gestion d'état
- CSS: Classes utilitaires pour interface visuelle

📡 INTERACTIONS:
- Parent: Réception données article/matières et fonction handleChange
- Parent: Notification modifications via callback onDataChange
- Interface: Sélection visuelle avec aperçu couleurs
- Stock: Gestion matières en stock vs calepinage

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface visuelle avec cartes matières et aperçu couleurs
- Synchronisation automatique option "en stock" entre faces identiques
- Mode personnalisé avec sélection indépendante par face
- Adaptation interface coussin vs bâche (assemblage)
- Conversion couleurs vers codes hexadécimaux pour aperçu
- Wrapper de notification pour synchronisation parent
*/
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Article, Matiere } from './types';
import { apiFetch } from '../../../services/httpClient';
import AuthContext from '../../../contexts/AuthContext';
import { addRecentMaterial, getRecentMaterials } from '../../../services/recentMaterialsService';
import RecentMaterialsGrid from './components/RecentMaterialsGrid';
import MaterialsCatalogModal from './components/MaterialsCatalogModal';

// ✅ ÉTAPE 1 : Interface Props modifiée
interface MateriauxProps {
  article: Article;
  matieres: Matiere[];
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section?: string, subsection?: string) => void;
  onDataChange: () => void; // ✅ NOUVELLE PROP AJOUTÉE
  featuredMaterialId?: string;
  onMatieresRefresh?: (matieres: Matiere[]) => void;
}

// Fonction pour convertir un nom de couleur en code hexadécimal
const getCouleurHex = (couleur: string): string => {
  const couleursMap: Record<string, string> = {
    'Blanc': '#FFFFFF',
    'Noir': '#000000',
    'Beige': '#F5F5DC',
    'Gris': '#808080',
    'Gris clair': '#D3D3D3',
    'Bleu marine': '#000080',
    'Vert': '#008000',
    // Ajouter d'autres couleurs au besoin
  };
  
  return couleursMap[couleur] || '#CCCCCC'; // Couleur par défaut si non trouvée
};

// ✅ ÉTAPE 2 : Composant modifié avec onDataChange
const Materiaux: React.FC<MateriauxProps> = ({
  article,
  matieres,
  handleChange,
  onDataChange, // ✅ NOUVELLE PROP AJOUTÉE
  featuredMaterialId,
  onMatieresRefresh,
}) => {
  const auth = useContext(AuthContext);
  const [storageScope, setStorageScope] = useState({ tenantId: "defaultTenant", userId: "anonymous" });
  const [recentMaterialIds, setRecentMaterialIds] = useState<string[]>([]);
  const [showCatalogModal, setShowCatalogModal] = useState(false);

  // ✅ ÉTAPE 3 : Fonction wrapper qui notifie les changements
  const handleChangeWithNotification = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section?: string, subsection?: string) => {
    handleChange(e, section, subsection);
    onDataChange(); // Notifier le parent que des données ont été modifiées
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    let fallbackUserId = "anonymous";
    const storedUser = window.localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        fallbackUserId = parsed.userId || fallbackUserId;
      } catch {
        // ignore parse errors
      }
    }
    const resolvedUserId = auth?.user?.userId || fallbackUserId;
    const resolvedTenant = window.localStorage.getItem("tenantId") || "defaultTenant";
    setStorageScope({ tenantId: resolvedTenant, userId: resolvedUserId });
  }, [auth?.user?.userId]);

  useEffect(() => {
    setRecentMaterialIds(getRecentMaterials(storageScope.tenantId, storageScope.userId));
  }, [storageScope]);

  const recordRecentMaterial = (materialId: string) => {
    addRecentMaterial(storageScope.tenantId, storageScope.userId, materialId);
    setRecentMaterialIds(getRecentMaterials(storageScope.tenantId, storageScope.userId));
  };

  const [personnaliser, setPersonnaliser] = useState<boolean>(false);
  const [matiereSelectionnee, setMatiereSelectionnee] = useState<string>(
    article.materiaux.dessus.matiere && 
    article.materiaux.dessus.matiere === article.materiaux.dessous.matiere && 
    article.materiaux.dessus.matiere === article.materiaux.cotes.matiere 
      ? article.materiaux.dessus.matiere 
      : ''
  );
  const [couleurSelectionnee, setCouleurSelectionnee] = useState<string>(
    article.materiaux.dessus.couleur && 
    article.materiaux.dessus.couleur === article.materiaux.dessous.couleur && 
    article.materiaux.dessus.couleur === article.materiaux.cotes.couleur 
      ? article.materiaux.dessus.couleur 
      : ''
  );

  // 🔍 NOUVEAUX ÉTATS POUR LES FILTRES
  const [filtreRecherche, setFiltreRecherche] = useState<string>('');
  const [filtreType, setFiltreType] = useState<string>('');
  const [filtreLaizeMin, setFiltreLaizeMin] = useState<string>('');
  const [filtreLaizeMax, setFiltreLaizeMax] = useState<string>('');
  const [filtreUsage, setFiltreUsage] = useState<{
    indoor: boolean;
    outdoor: boolean;
    nautisme: boolean;
    medical: boolean;
  }>({ indoor: true, outdoor: true, nautisme: true, medical: true });
  const [filtreCouleur, setFiltreCouleur] = useState<string>('');
  const [filtreFournisseur, setFiltreFournisseur] = useState<{ [key: string]: boolean }>({});
  const [favorisLocaux, setFavorisLocaux] = useState<{ [key: string]: boolean }>({});

  // 🔍 Fonction pour toggle le favori d'une matière
  const toggleFavori = async (matiereId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Empêcher la sélection de la matière
    
    // Optimistic update : mettre à jour l'affichage immédiatement
    const matiereActuelle = matieres.find(m => m.id === matiereId);
    const nouveauStatut = !(favorisLocaux[matiereId] ?? matiereActuelle?.favori ?? false);
    setFavorisLocaux(prev => ({ ...prev, [matiereId]: nouveauStatut }));
    
    try {
      await apiFetch(`/matieres/${matiereId}/favori`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
        },
        body: JSON.stringify({ favori: nouveauStatut })
      });
    } catch (error) {
      // En cas d'erreur, revenir à l'état précédent
      setFavorisLocaux(prev => ({ ...prev, [matiereId]: !nouveauStatut }));
      console.error('❌ Erreur toggle favori:', error);
    }
  };

  // 🔍 DIAGNOSTIC LOGS - Au début du composant




  console.log('matieres disponibles:', matieres.map(m => ({id: m.id, type: typeof m.id, nom: m.nom})));

  // Réinitialiser les états locaux lorsque l'article change
  React.useEffect(() => {
    // 🔍 DIAGNOSTIC LOGS - Dans useEffect



    // Vérifier si l'article a été réinitialisé (toutes les matières et couleurs sont vides)
    if (!article.materiaux.dessus.matiere && !article.materiaux.dessous.matiere && !article.materiaux.cotes.matiere) {
      // Réinitialiser les états locaux
      setMatiereSelectionnee('');
      setCouleurSelectionnee('');
      setPersonnaliser(false);
    } else {
      // Mettre à jour les états locaux en fonction des matières et couleurs de l'article
      const matiereCommune = 
        article.materiaux.dessus.matiere && 
        article.materiaux.dessus.matiere === article.materiaux.dessous.matiere && 
        article.materiaux.dessus.matiere === article.materiaux.cotes.matiere 
          ? article.materiaux.dessus.matiere 
          : '';
      
      const couleurCommune = 
        article.materiaux.dessus.couleur && 
        article.materiaux.dessus.couleur === article.materiaux.dessous.couleur && 
        article.materiaux.dessus.couleur === article.materiaux.cotes.couleur 
          ? article.materiaux.dessus.couleur 
          : '';
      
      setMatiereSelectionnee(matiereCommune);
      setCouleurSelectionnee(couleurCommune);
      
      // 🔍 DIAGNOSTIC LOGS - Après calcul



      // Ne pas basculer automatiquement en mode personnalisation
      // Laisser l'utilisateur décider quand il veut personnaliser
      // setPersonnaliser(!matiereCommune || !couleurCommune);
    }
  }, [article.materiaux]);

  // ✅ FONCTIONS MODIFIÉES : Ajouter onDataChange() à toutes les fonctions qui modifient les données
  
  // Fonction pour appliquer la même matière à toutes les faces de manière synchrone
  const appliquerMatiere = (matiereId: string, matiereSource?: Matiere) => {
    
    // Mettre à jour l'état local
    setMatiereSelectionnee(matiereId);
    
    // Récupérer la matière sélectionnée
    const matiereObj = matiereSource || matieres.find(m => m.id === matiereId);
    if (!matiereObj) {
      console.error("Matière non trouvée:", matiereId);
      return;
    }
    
    // Appliquer la matière directement à l'objet article pour chaque face
    article.materiaux.dessus.matiere = matiereId;
    article.materiaux.dessous.matiere = matiereId;
    article.materiaux.cotes.matiere = matiereId;
    
    // Puis utiliser handleChange pour notifier le composant parent
    const faces = ['dessus', 'dessous', 'cotes'];
    faces.forEach(face => {
      handleChangeWithNotification({ // ✅ Utilisation du wrapper
        target: {
          name: 'matiere',
          value: matiereId,
          type: 'select-one'
        }
      } as React.ChangeEvent<HTMLSelectElement>, 'materiaux', face);
    });

    recordRecentMaterial(matiereId);

    // Auto-sélectionner la couleur si la matière ne dispose que d'une couleur
    if (matiereObj.couleurs && matiereObj.couleurs.length === 1) {
      appliquerCouleur(matiereObj.couleurs[0], matiereId, matiereObj);
    }
  };

  // Fonction pour appliquer la même couleur à toutes les faces de manière synchrone
  const appliquerCouleur = (couleur: string, matiereIdOverride?: string, matiereSource?: Matiere) => {
    
    // Mettre à jour l'état local de la couleur sélectionnée
    setCouleurSelectionnee(couleur);
    
    // Vérifier si une matière est sélectionnée
    const matiereId = matiereIdOverride || matiereSelectionnee || matiereCommune;
    
    if (!matiereId) {
      console.error("Aucune matière sélectionnée");
      alert("Veuillez d'abord sélectionner une matière");
      return;
    }
    
    // Récupérer la matière sélectionnée
    const matiereObj = matiereSource || matieres.find(m => m.id === matiereId);
    if (!matiereObj) {
      console.error("Matière non trouvée:", matiereId);
      return;
    }
    
    if (!matiereObj.couleurs.includes(couleur)) {
      console.error(`Couleur ${couleur} non disponible dans la matière ${matiereObj.nom}`);
      return;
    }
    
    // Appliquer la couleur directement à l'objet article pour chaque face
    article.materiaux.dessus.couleur = couleur;
    article.materiaux.dessous.couleur = couleur;
    article.materiaux.cotes.couleur = couleur;
    
    // Puis utiliser handleChange pour notifier le composant parent
    const faces = ['dessus', 'dessous', 'cotes'];
    faces.forEach(face => {
      handleChangeWithNotification({ // ✅ Utilisation du wrapper
        target: {
          name: 'couleur',
          value: couleur,
          type: 'select-one'
        }
      } as React.ChangeEvent<HTMLSelectElement>, 'materiaux', face);
    });
  };
  
  // Fonction pour appliquer l'option "Matière en stock" à toutes les faces
  const appliquerEnStock = (enStock: boolean) => {

    // Appliquer l'option directement à l'objet article pour chaque face
    if (article.materiaux.dessus.matiere) article.materiaux.dessus.enStock = enStock;
    if (article.materiaux.dessous.matiere) article.materiaux.dessous.enStock = enStock;
    if (article.materiaux.cotes.matiere) article.materiaux.cotes.enStock = enStock;
    
    // Puis utiliser handleChange pour notifier le composant parent
    const faces = ['dessus', 'dessous', 'cotes'];
    faces.forEach(face => {
      if (article.materiaux[face as keyof typeof article.materiaux].matiere) {

        handleChangeWithNotification({ // ✅ Utilisation du wrapper
          target: {
            name: 'enStock',
            checked: enStock,
            type: 'checkbox'
          }
        } as React.ChangeEvent<HTMLInputElement>, 'materiaux', face);
      }
    });
  };

  // Déterminer la matière et la couleur communes (si toutes les faces ont la même)
  const matiereCommune = 
    article.materiaux.dessus.matiere && 
    article.materiaux.dessus.matiere === article.materiaux.dessous.matiere && 
    article.materiaux.dessus.matiere === article.materiaux.cotes.matiere 
      ? article.materiaux.dessus.matiere 
      : '';
  
  const couleurCommune = 
    article.materiaux.dessus.couleur && 
    article.materiaux.dessus.couleur === article.materiaux.dessous.couleur && 
    article.materiaux.dessus.couleur === article.materiaux.cotes.couleur 
      ? article.materiaux.dessus.couleur 
      : '';

  // Obtenir la matière sélectionnée
  const matiereObj = matieres.find(m => m.id === matiereSelectionnee || m.id === matiereCommune);

  // 🔍 EXTRACTION DES VALEURS UNIQUES POUR LES FILTRES
  const typesUniques = Array.from(new Set(matieres.map(m => m.type).filter(Boolean))).sort();
  const couleursUniques = Array.from(new Set(matieres.flatMap(m => m.couleurs || []))).sort();
  
  // Extraire les fournisseurs uniques + ajouter une catégorie pour les matières sans fournisseur
  const fournisseursSet = new Set(matieres.map(m => m.fournisseur).filter(Boolean));
  const hasMatieresSansFournisseur = matieres.some(m => !m.fournisseur);
  const hasFavoris = matieres.some(m => m.favori);
  const fournisseursUniques = Array.from(fournisseursSet).sort();
  
  // Ajouter "⭐ Mes favoris" en premier si des favoris existent
  if (hasFavoris) {
    fournisseursUniques.unshift('⭐ Mes favoris');
  }
  
  // Ajouter la catégorie "Mes matières" après les favoris si des matières sans fournisseur existent
  if (hasMatieresSansFournisseur) {
    // Insérer après "⭐ Mes favoris" si présent, sinon en premier
    const insertIndex = hasFavoris ? 1 : 0;
    fournisseursUniques.splice(insertIndex, 0, 'Mes matières');
  }

  // 🔍 CALCUL DES VALEURS MIN/MAX POUR LES LAIZES (pour affichage indicatif)
  const laizeMin = Math.min(...matieres.map(m => m.laize).filter(Boolean));
  const laizeMax = Math.max(...matieres.map(m => m.laize).filter(Boolean));

  // 🔍 VÉRIFIER SI DES DONNÉES D'USAGE SONT DISPONIBLES
  const hasUsageData = matieres.some(m => 
    m.usage_indoor !== undefined || 
    m.usage_outdoor !== undefined || 
    m.usage_nautisme !== undefined || 
    m.usage_medical !== undefined
  );

  const selectedMaterialId = matiereSelectionnee || matiereCommune;

  const quickMaterials = useMemo(() => {
    const seen = new Set<string>();
    const list: Matiere[] = [];
    const add = (m?: Matiere) => {
      if (m && !seen.has(m.id)) {
        list.push(m);
        seen.add(m.id);
      }
    };

    // Forcer la présence de la matière sélectionnée, même si elle n'est pas dans les récents/favoris
    add(matieres.find((m) => m.id === selectedMaterialId));

    recentMaterialIds.forEach((id) => add(matieres.find((m) => m.id === id)));
    matieres
      .filter((m) => (favorisLocaux[m.id] ?? m.favori) === true)
      .forEach((m) => add(m));
    matieres.forEach((m) => add(m));

    return list.slice(0, 6);
  }, [favorisLocaux, matieres, recentMaterialIds]);

  const featuredMaterial = useMemo(() => {
    if (!featuredMaterialId) return undefined;
    return matieres.find((m) => m.id === featuredMaterialId);
  }, [featuredMaterialId, matieres]);

  // 🔍 INITIALISER L'ÉTAT DU FILTRE FOURNISSEUR avec tous les fournisseurs cochés par défaut
  React.useEffect(() => {
    if (fournisseursUniques.length > 0 && Object.keys(filtreFournisseur).length === 0) {
      const initialState: { [key: string]: boolean } = {};
      fournisseursUniques.forEach(f => {
        if (f) { // Vérifier que f n'est pas undefined
          initialState[f] = true; // Tous cochés par défaut (afficher toutes les matières)
        }
      });
      setFiltreFournisseur(initialState);
    }
  }, [fournisseursUniques.length]);

  // 🔍 APPLICATION DES FILTRES
  const matieresFiltered = matieres.filter(matiere => {
    // Filtre recherche textuelle (nom)
    if (filtreRecherche && !matiere.nom.toLowerCase().includes(filtreRecherche.toLowerCase())) {
      return false;
    }

    // Filtre type
    if (filtreType && matiere.type !== filtreType) {
      return false;
    }

    // Filtre laize minimum
    if (filtreLaizeMin) {
      const min = parseFloat(filtreLaizeMin);
      if (!isNaN(min) && matiere.laize < min) {
        return false;
      }
    }

    // Filtre laize maximum
    if (filtreLaizeMax) {
      const max = parseFloat(filtreLaizeMax);
      if (!isNaN(max) && matiere.laize > max) {
        return false;
      }
    }

    // Filtre usage (Indoor/Outdoor/Nautisme/Medical) - Logique OU additive
    // Si au moins un filtre est décoché (donc filtrage actif)
    const usageFiltersActive = !filtreUsage.indoor || !filtreUsage.outdoor || !filtreUsage.nautisme || !filtreUsage.medical;
    
    if (usageFiltersActive) {
      // La matière doit avoir AU MOINS UN des usages cochés pour être affichée
      const matchesUsage = 
        (filtreUsage.indoor && matiere.usage_indoor) ||
        (filtreUsage.outdoor && matiere.usage_outdoor) ||
        (filtreUsage.nautisme && matiere.usage_nautisme) ||
        (filtreUsage.medical && matiere.usage_medical);
      
      if (!matchesUsage) return false;
    }

    // Filtre couleur (vérifier si la matière contient la couleur sélectionnée)
    if (filtreCouleur && !matiere.couleurs.some(c => c.toLowerCase().includes(filtreCouleur.toLowerCase()))) {
      return false;
    }

    // Filtre fournisseur - Logique OU sélective avec gestion des favoris
    // Si au moins un fournisseur est coché, afficher SEULEMENT les matières de ces fournisseurs
    const fournisseursCoches = Object.entries(filtreFournisseur).filter(([_, checked]) => checked).map(([f, _]) => f);
    
    if (fournisseursCoches.length > 0) {
      const mesFavorisCoche = fournisseursCoches.includes('⭐ Mes favoris');
      const mesMatieresCoche = fournisseursCoches.includes('Mes matières');
      
      // Si SEULEMENT "⭐ Mes favoris" est coché → afficher uniquement les favoris
      if (mesFavorisCoche && fournisseursCoches.length === 1) {
        if (!matiere.favori) return false;
      }
      // Si "⭐ Mes favoris" ET d'autres filtres → afficher favoris OU les autres critères
      else if (mesFavorisCoche) {
        // La matière doit être soit favori, soit correspondre aux autres critères
        const estFavori = matiere.favori;
        const correspondAutreCritere = 
          (!matiere.fournisseur && mesMatieresCoche) ||
          (matiere.fournisseur && fournisseursCoches.includes(matiere.fournisseur));
        
        if (!estFavori && !correspondAutreCritere) return false;
      }
      // Sinon, logique normale sans favoris
      else {
        // Cas 1: Matière sans fournisseur → afficher si "Mes matières" est coché
        if (!matiere.fournisseur) {
          if (!mesMatieresCoche) return false;
        }
        // Cas 2: Matière avec fournisseur → afficher si son fournisseur est coché
        else {
          if (!fournisseursCoches.includes(matiere.fournisseur)) {
            return false;
          }
        }
      }
    }

    return true;
  });

  // 🔍 FONCTION DE RÉINITIALISATION DES FILTRES
  const reinitialiserFiltres = () => {
    setFiltreRecherche('');
    setFiltreType('');
    setFiltreLaizeMin('');
    setFiltreLaizeMax('');
    setFiltreUsage({ indoor: false, outdoor: false, nautisme: false, medical: false });
    setFiltreCouleur('');
    // Réinitialiser tous les fournisseurs (décocher toutes les cases)
    const resetFournisseurs: { [key: string]: boolean } = {};
    fournisseursUniques.forEach(f => {
      if (f) resetFournisseurs[f] = false;
    });
    setFiltreFournisseur(resetFournisseurs);
  };

  // 🔍 VÉRIFIER SI DES FILTRES SONT ACTIFS
  const fournisseurActif = Object.values(filtreFournisseur).some(checked => checked);
  const filtresActifs = Boolean(
    filtreRecherche ||
      filtreType ||
      filtreLaizeMin ||
      filtreLaizeMax ||
      filtreUsage.indoor ||
      filtreUsage.outdoor ||
      filtreUsage.nautisme ||
      filtreUsage.medical ||
      filtreCouleur ||
      fournisseurActif
  );

  return (
    <>
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Matériaux</h2>
          <p className="text-sm text-gray-500">Sélection rapide ou catalogue complet</p>
        </div>
        <RecentMaterialsGrid
          materials={quickMaterials}
          featuredMaterial={featuredMaterial}
          selectedMaterialId={selectedMaterialId}
          onSelect={(id) => {
            appliquerMatiere(id);
            setShowCatalogModal(false);
          }}
          onOpenCatalog={() => setShowCatalogModal(true)}
        />

        {/* Interface différente selon le type de produit */}
        {article.type === 'bache' ? (
        // Interface simplifiée pour les bâches
        <div className="space-y-6">
          {!matiereObj && (
            <div className="border border-dashed border-gray-300 rounded-lg p-4 text-sm text-gray-600 bg-gray-50">
              Selectionnez une matiere via la selection rapide ou ouvrez le catalogue.
            </div>
          )}
          {/* Sélection visuelle des couleurs */}
          {matiereObj && (
            <div>
              <label className="form-label block mb-2">Couleur</label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {matiereObj.couleurs.map((couleur) => (
                  <div 
                    key={couleur}
                    className={`border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md flex flex-col items-center ${
                      couleurCommune === couleur || couleurSelectionnee === couleur
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50' 
                        : 'border-gray-200 hover:border-primary-300'
                    }`}
                    onClick={() => appliquerCouleur(couleur)}
                  >
                    <div className="relative">
                      <div 
                        className="w-10 h-10 rounded-full mb-1 border border-gray-300" 
                        style={{ 
                          backgroundColor: getCouleurHex(couleur),
                          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
                        }}
                      />
                      {(couleurCommune === couleur || couleurSelectionnee === couleur) && (
                        <div className="absolute -top-1 -right-1 bg-primary-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-center font-medium">{couleur}</span>
                    {(couleurCommune === couleur || couleurSelectionnee === couleur) && (
                      <span className="text-xs text-primary-600 font-medium">Sélectionnée</span>
                    )}
                  </div>
                ))}
              </div>
              
                {/* Option "Matière en stock" */}
                {(couleurCommune || couleurSelectionnee) && (
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        id="matiere-en-stock"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={
                          article.materiaux.dessus.enStock === true &&
                          article.materiaux.dessous.enStock === true &&
                          article.materiaux.cotes.enStock === true
                        }
                        onChange={(e) => appliquerEnStock(e.target.checked)}
                      />
                      <label htmlFor="matiere-en-stock" className="ml-2 block text-sm text-gray-900">
                        Matière en stock
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Cochez cette case si vous disposez déjà de cette matière en stock. Sinon, le système calculera le coût en fonction minimum à la commande.
                    </p>
                  </div>
                )}
                
                {/* Affichage de débogage pour les couleurs disponibles */}
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                  <div>Matière sélectionnée: {matiereObj.nom}</div>
                  <div>Couleurs disponibles: {matiereObj.couleurs.join(', ')}</div>
                  <div>Couleur commune: {couleurCommune || 'Aucune'}</div>
                  <div>En stock: {
                    article.materiaux.dessus.enStock === true &&
                    article.materiaux.dessous.enStock === true &&
                    article.materiaux.cotes.enStock === true ? 'Oui' : 'Non'
                  }</div>
                </div>
            </div>
          )}
          
          {/* Note sur l'assemblage pour les grandes bâches */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800">Information sur l'assemblage</h4>
            <p className="text-sm text-blue-700">
              Les bâches de grande taille peuvent nécessiter un assemblage de plusieurs pièces si leurs dimensions dépassent la laize du matériau.
            </p>
          </div>
        </div>
      ) : (
        // Interface standard pour les coussins
        !personnaliser ? (
          <div className="space-y-6">
            {!matiereObj && (
              <div className="border border-dashed border-gray-300 rounded-lg p-4 text-sm text-gray-600 bg-gray-50">
                Selectionnez une matiere via la selection rapide ou ouvrez le catalogue.
              </div>
            )}
            {/* Sélection visuelle des couleurs */}
            {matiereObj && (
              <div>
                <label className="form-label block mb-2">Couleur (toutes faces)</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {matiereObj.couleurs.map((couleur) => (
                    <div 
                      key={couleur}
                      className={`border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md flex flex-col items-center ${
                        couleurCommune === couleur || couleurSelectionnee === couleur
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50' 
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                      onClick={() => appliquerCouleur(couleur)}
                    >
                      <div className="relative">
                        <div 
                          className="w-10 h-10 rounded-full mb-1 border border-gray-300" 
                          style={{ 
                            backgroundColor: getCouleurHex(couleur),
                            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
                          }}
                        />
                        {(couleurCommune === couleur || couleurSelectionnee === couleur) && (
                          <div className="absolute -top-1 -right-1 bg-primary-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-center font-medium">{couleur}</span>
                      {(couleurCommune === couleur || couleurSelectionnee === couleur) && (
                        <span className="text-xs text-primary-600 font-medium">Sélectionnée</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Option "Matière en stock" - Déplacée avant la section grille */}
                {(couleurCommune || couleurSelectionnee) && (
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        id="matiere-en-stock-coussin"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={
                          article.materiaux.dessus.enStock === true &&
                          article.materiaux.dessous.enStock === true &&
                          article.materiaux.cotes.enStock === true
                        }
                        onChange={(e) => appliquerEnStock(e.target.checked)}
                      />
                      <label htmlFor="matiere-en-stock-coussin" className="ml-2 block text-sm text-gray-900">
                        Matière en stock
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Cochez cette case si vous disposez déjà de cette matière en stock. Sinon, le système calculera le coût en fonction du calepinage.
                    </p>
                  </div>
                )}

                {/* Section grille (si option grille activée) */}
                {article.options.grille && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-md font-medium text-blue-800 mb-3">
                      🔲 Matière grille de drainage
                    </h4>
                    
                    {/* Sélection matière grille */}
                    <div className="mb-4">
                      <label className="form-label block mb-2">Matière grille</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {matieres
                          .filter(matiere => matiere.type.toLowerCase().includes('mesh') || matiere.type.toLowerCase().includes('grille'))
                          .map((matiere) => (
                            <div 
                              key={matiere.id}
                              className={`border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md ${
                                article.options.grilleMatiere === matiere.id
                                  ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-500 ring-opacity-50' 
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                              onClick={() => {
                                const event = {
                                  target: {
                                    name: 'grilleMatiere',
                                    value: matiere.id,
                                    type: 'select'
                                  }
                                } as unknown as React.ChangeEvent<HTMLSelectElement>;
                                handleChangeWithNotification(event, 'options');
                              }}
                            >
                              <div className="font-medium text-gray-800">{matiere.nom}</div>
                              <div className="text-sm text-gray-500">{matiere.type}</div>
                              <div className="text-sm text-gray-500">{(matiere.prix * 1.2).toFixed(2)}€ TTC/ML</div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Sélection couleur grille */}
                    {article.options.grilleMatiere && (
                      <div>
                        <label className="form-label block mb-2">Couleur grille</label>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                          {matieres
                            .find(m => m.id === article.options.grilleMatiere)
                            ?.couleurs.map((couleur) => (
                              <div 
                                key={couleur}
                                className={`border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md flex flex-col items-center ${
                                  article.options.grilleCouleur === couleur
                                    ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-500 ring-opacity-50' 
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                                onClick={() => {
                                  const event = {
                                    target: {
                                      name: 'grilleCouleur',
                                      value: couleur,
                                      type: 'select'
                                    }
                                  } as unknown as React.ChangeEvent<HTMLSelectElement>;
                                  handleChangeWithNotification(event, 'options');
                                }}
                              >
                                <div className="relative">
                                  <div 
                                    className="w-8 h-8 rounded-full mb-1 border border-gray-300" 
                                    style={{ 
                                      backgroundColor: getCouleurHex(couleur),
                                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
                                    }}
                                  />
                                  {article.options.grilleCouleur === couleur && (
                                    <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs text-center font-medium">{couleur}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Option "Matière grille en stock" */}
                    {article.options.grilleMatiere && article.options.grilleCouleur && (
                      <div className="mt-4">
                        <div className="flex items-center">
                          <input
                            id="grille-en-stock"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={article.options.grilleEnStock === true}
                            onChange={(e) => {
                              const event = {
                                target: {
                                  name: 'grilleEnStock',
                                  checked: e.target.checked,
                                  type: 'checkbox'
                                }
                              } as unknown as React.ChangeEvent<HTMLInputElement>;
                              handleChangeWithNotification(event, 'options');
                            }}
                          />
                          <label htmlFor="grille-en-stock" className="ml-2 block text-sm text-blue-900">
                            Matière grille en stock
                          </label>
                        </div>
                        <p className="mt-1 text-xs text-blue-600">
                          Cochez si vous disposez déjà de cette matière grille en stock.
                        </p>
                      </div>
                    )}

                    {/* Information composition selon forme */}
                    <div className="mt-3 p-2 bg-white rounded border text-xs text-gray-600">
                      {article.dimensions?.forme === 'rond_bandeau' ? (
                        <div>🔵 <strong>Forme ronde :</strong> fond entièrement remplacé par du mesh</div>
                      ) : (
                        <div>🔲 <strong>Forme rectangulaire :</strong> grille mesh centrale + 3 bandes fond de fixation</div>
                      )}
                    </div>
                  </div>
                )}
                
              {/* Affichage de débogage pour les couleurs disponibles */}
              <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                <div>Matière sélectionnée: {matiereObj.nom}</div>
                <div>Couleurs disponibles: {matiereObj.couleurs.join(', ')}</div>
                <div>Couleur commune: {couleurCommune || 'Aucune'}</div>
                <div>En stock: {
                  article.materiaux.dessus.enStock === true &&
                  article.materiaux.dessous.enStock === true &&
                  article.materiaux.cotes.enStock === true ? 'Oui' : 'Non'
                }</div>
              </div>
              </div>
            )}
            
            {/* Bouton pour personnaliser */}
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => {
                  // Récupérer la matière et la couleur actuelles
                  const matiereId = matiereSelectionnee || matiereCommune;
                  const couleur = couleurSelectionnee || couleurCommune;


                  // Appliquer la matière et la couleur à toutes les faces avant de passer en mode personnalisation
                  if (matiereId) {
                    // Appliquer d'abord la matière à toutes les faces
                    appliquerMatiere(matiereId);
                    
                    // Puis appliquer la couleur si disponible
                    if (couleur) {
                      // Vérifier que la couleur est disponible pour la matière
                      const matiereObj = matieres.find(m => m.id === matiereId);
                      if (matiereObj && matiereObj.couleurs.includes(couleur)) {
                        appliquerCouleur(couleur);
                      }
                    }
                  }
                  
                  // Passer en mode personnalisation après avoir appliqué la matière et la couleur
                  setTimeout(() => {
                    setPersonnaliser(true);
                  }, 100);
                }}
              >
                Personnaliser par face
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Dessus */}
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">Dessus</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="dessus-matiere" className="form-label">
                      Matière
                    </label>
                    <select
                      id="dessus-matiere"
                      name="matiere"
                      className="input-field"
                      value={article.materiaux.dessus.matiere || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'dessus')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                    >
                      <option value="">Sélectionner une matière</option>
                      {matieres.map((matiere) => (
                        <option key={matiere.id} value={matiere.id}>
                          {matiere.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="dessus-couleur" className="form-label">
                      Couleur
                    </label>
                    <select
                      id="dessus-couleur"
                      name="couleur"
                      className="input-field"
                      value={article.materiaux.dessus.couleur || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'dessus')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                      disabled={!article.materiaux.dessus.matiere}
                    >
                      <option value="">Sélectionner une couleur</option>
                      {article.materiaux.dessus.matiere && 
                        matieres
                          .find((m) => m.id === article.materiaux.dessus.matiere)
                          ?.couleurs.map((couleur) => (
                            <option key={couleur} value={couleur}>
                              {couleur}
                            </option>
                          ))}
                    </select>
                  </div>
                  {article.materiaux.dessus.matiere && article.materiaux.dessus.couleur && (
                    <div className="mt-2">
                      <div className="flex items-center">
                        <input
                          id="dessus-en-stock"
                          name="enStock"
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          checked={article.materiaux.dessus.enStock === true}
                          onChange={(e) => {
                            // Récupérer la matière et la couleur de cette face
                            const matiere = article.materiaux.dessus.matiere;
                            const couleur = article.materiaux.dessus.couleur;
                            const enStock = e.target.checked;
                            
                            // Mettre à jour directement l'objet pour cette face
                            article.materiaux.dessus.enStock = enStock;
                            
                            // Propager l'état "en stock" aux autres faces avec la même matière et couleur
                            if (article.materiaux.dessous.matiere === matiere && 
                                article.materiaux.dessous.couleur === couleur) {
                              article.materiaux.dessous.enStock = enStock;
                              // Notifier le composant parent pour la face dessous
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'dessous');
                            }
                            
                            if (article.materiaux.cotes.matiere === matiere && 
                                article.materiaux.cotes.couleur === couleur) {
                              article.materiaux.cotes.enStock = enStock;
                              // Notifier le composant parent pour la face cotes
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'cotes');
                            }
                            
                            // Notifier le composant parent pour cette face
                            handleChangeWithNotification(e, 'materiaux', 'dessus'); // ✅ Utilisation du wrapper
                          }}
                        />
                        <label htmlFor="dessus-en-stock" className="ml-2 block text-sm text-gray-900">
                          Matière en stock
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Dessous */}
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">Dessous</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="dessous-matiere" className="form-label">
                      Matière
                    </label>
                    <select
                      id="dessous-matiere"
                      name="matiere"
                      className="input-field"
                      value={article.materiaux.dessous.matiere || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'dessous')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                    >
                      <option value="">Sélectionner une matière</option>
                      {matieres.map((matiere) => (
                        <option key={matiere.id} value={matiere.id}>
                          {matiere.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="dessous-couleur" className="form-label">
                      Couleur
                    </label>
                    <select
                      id="dessous-couleur"
                      name="couleur"
                      className="input-field"
                      value={article.materiaux.dessous.couleur || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'dessous')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                      disabled={!article.materiaux.dessous.matiere}
                    >
                      <option value="">Sélectionner une couleur</option>
                      {article.materiaux.dessous.matiere && 
                        matieres
                          .find((m) => m.id === article.materiaux.dessous.matiere)
                          ?.couleurs.map((couleur) => (
                            <option key={couleur} value={couleur}>
                              {couleur}
                            </option>
                          ))}
                    </select>
                  </div>
                  {article.materiaux.dessous.matiere && article.materiaux.dessous.couleur && (
                    <div className="mt-2">
                      <div className="flex items-center">
                        <input
                          id="dessous-en-stock"
                          name="enStock"
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          checked={article.materiaux.dessous.enStock === true}
                          onChange={(e) => {
                            // Récupérer la matière et la couleur de cette face
                            const matiere = article.materiaux.dessous.matiere;
                            const couleur = article.materiaux.dessous.couleur;
                            const enStock = e.target.checked;
                            
                            // Mettre à jour directement l'objet pour cette face
                            article.materiaux.dessous.enStock = enStock;
                            
                            // Propager l'état "en stock" aux autres faces avec la même matière et couleur
                            if (article.materiaux.dessus.matiere === matiere && 
                                article.materiaux.dessus.couleur === couleur) {
                              article.materiaux.dessus.enStock = enStock;
                              // Notifier le composant parent pour la face dessus
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'dessus');
                            }
                            
                            if (article.materiaux.cotes.matiere === matiere && 
                                article.materiaux.cotes.couleur === couleur) {
                              article.materiaux.cotes.enStock = enStock;
                              // Notifier le composant parent pour la face cotes
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'cotes');
                            }
                            
                            // Notifier le composant parent pour cette face
                            handleChangeWithNotification(e, 'materiaux', 'dessous'); // ✅ Utilisation du wrapper
                          }}
                        />
                        <label htmlFor="dessous-en-stock" className="ml-2 block text-sm text-gray-900">
                          Matière en stock
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Côtés */}
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">
                  Côtés {article.forme.includes('joues') ? '(ou joues)' : ''}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="cotes-matiere" className="form-label">
                      Matière
                    </label>
                    <select
                      id="cotes-matiere"
                      name="matiere"
                      className="input-field"
                      value={article.materiaux.cotes.matiere || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'cotes')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                    >
                      <option value="">Sélectionner une matière</option>
                      {matieres.map((matiere) => (
                        <option key={matiere.id} value={matiere.id}>
                          {matiere.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cotes-couleur" className="form-label">
                      Couleur
                    </label>
                    <select
                      id="cotes-couleur"
                      name="couleur"
                      className="input-field"
                      value={article.materiaux.cotes.couleur || ''}
                      onChange={(e) => handleChangeWithNotification(e, 'materiaux', 'cotes')} // ✅ ÉTAPE 4 : Utilisation du wrapper
                      required
                      disabled={!article.materiaux.cotes.matiere}
                    >
                      <option value="">Sélectionner une couleur</option>
                      {article.materiaux.cotes.matiere && 
                        matieres
                          .find((m) => m.id === article.materiaux.cotes.matiere)
                          ?.couleurs.map((couleur) => (
                            <option key={couleur} value={couleur}>
                              {couleur}
                            </option>
                          ))}
                    </select>
                  </div>
                  {article.materiaux.cotes.matiere && article.materiaux.cotes.couleur && (
                    <div className="mt-2">
                      <div className="flex items-center">
                        <input
                          id="cotes-en-stock"
                          name="enStock"
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          checked={article.materiaux.cotes.enStock === true}
                          onChange={(e) => {
                            // Récupérer la matière et la couleur de cette face
                            const matiere = article.materiaux.cotes.matiere;
                            const couleur = article.materiaux.cotes.couleur;
                            const enStock = e.target.checked;
                            
                            // Mettre à jour directement l'objet pour cette face
                            article.materiaux.cotes.enStock = enStock;
                            
                            // Propager l'état "en stock" aux autres faces avec la même matière et couleur
                            if (article.materiaux.dessus.matiere === matiere && 
                                article.materiaux.dessus.couleur === couleur) {
                              article.materiaux.dessus.enStock = enStock;
                              // Notifier le composant parent pour la face dessus
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'dessus');
                            }
                            
                            if (article.materiaux.dessous.matiere === matiere && 
                                article.materiaux.dessous.couleur === couleur) {
                              article.materiaux.dessous.enStock = enStock;
                              // Notifier le composant parent pour la face dessous
                              handleChangeWithNotification({ // ✅ Utilisation du wrapper
                                target: {
                                  name: 'enStock',
                                  checked: enStock,
                                  type: 'checkbox'
                                }
                              } as React.ChangeEvent<HTMLInputElement>, 'materiaux', 'dessous');
                            }
                            
                            // Notifier le composant parent pour cette face
                            handleChangeWithNotification(e, 'materiaux', 'cotes'); // ✅ Utilisation du wrapper
                          }}
                        />
                        <label htmlFor="cotes-en-stock" className="ml-2 block text-sm text-gray-900">
                          Matière en stock
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Bouton pour revenir au mode simple */}
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => {
                  // Réinitialiser les sélections de matière et de couleur
                  setMatiereSelectionnee('');
                  setCouleurSelectionnee('');
                  
                  // Réinitialiser les matières et couleurs pour toutes les faces
                  const faces = ['dessus', 'dessous', 'cotes'];
                  faces.forEach(face => {
                    // Réinitialiser la matière
                    handleChangeWithNotification({ // ✅ Utilisation du wrapper
                      target: {
                        name: 'matiere',
                        value: '',
                        type: 'select-one'
                      }
                    } as React.ChangeEvent<HTMLSelectElement>, 'materiaux', face);
                    
                    // Réinitialiser la couleur
                    handleChangeWithNotification({ // ✅ Utilisation du wrapper
                      target: {
                        name: 'couleur',
                        value: '',
                        type: 'select-one'
                      }
                    } as React.ChangeEvent<HTMLSelectElement>, 'materiaux', face);
                  });
                  
                  // Passer en mode non personnalisé
                  setPersonnaliser(false);
                }}
              >
                Utiliser même matière pour toutes les faces
              </button>
            </div>
          </div>
        )
      )}
      </div>
      <MaterialsCatalogModal
        isOpen={showCatalogModal}
        onClose={() => setShowCatalogModal(false)}
        matieres={matieres}
        matieresFiltered={matieresFiltered}
        filtresActifs={filtresActifs}
        filtreRecherche={filtreRecherche}
        setFiltreRecherche={setFiltreRecherche}
        filtreType={filtreType}
        setFiltreType={setFiltreType}
        filtreLaizeMin={filtreLaizeMin}
        setFiltreLaizeMin={setFiltreLaizeMin}
        filtreLaizeMax={filtreLaizeMax}
        setFiltreLaizeMax={setFiltreLaizeMax}
        filtreUsage={filtreUsage}
        setFiltreUsage={setFiltreUsage}
        filtreCouleur={filtreCouleur}
        setFiltreCouleur={setFiltreCouleur}
        filtreFournisseur={filtreFournisseur}
        setFiltreFournisseur={setFiltreFournisseur}
        typesUniques={typesUniques}
        couleursUniques={couleursUniques}
        fournisseursUniques={fournisseursUniques}
        hasUsageData={hasUsageData}
        laizeMin={laizeMin}
        laizeMax={laizeMax}
        reinitialiserFiltres={reinitialiserFiltres}
        favorisLocaux={favorisLocaux}
        toggleFavori={toggleFavori}
        onSelectMaterial={(id, defaultColor, matiereObjFromModal) => {
          appliquerMatiere(id, matiereObjFromModal);
          if (defaultColor) {
            appliquerCouleur(defaultColor, id, matiereObjFromModal);
          }
          setShowCatalogModal(false);
        }}
        onMatieresRefresh={onMatieresRefresh}
        selectedMaterialId={selectedMaterialId}
      />
    </>
  );
};

export default Materiaux;
